import { formatMonthName } from '@budgie/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { formatPeriodRange, shiftPeriod } from '../../lib/period';

type Props = {
  year: number;
  month: number;
  onChange: (year: number, month: number) => void;
  /** First and last day of the shown period, e.g. 20 sep – 19 okt */
  range?: { startDate: string; lastDate: string } | undefined;
  /** Latest period that may be opened (usually the one after the current) */
  max?: { year: number; month: number } | undefined;
  /** Extra line under the range, e.g. "dag 18 av 30" */
  meta?: ReactNode;
};

/** Page header for period-based screens: month name, its date range, prev/next. */
export function MonthNavigator({ year, month, onChange, range, max, meta }: Props) {
  const next = shiftPeriod(year, month, 1);
  const atMax = max != null && (year > max.year || (year === max.year && month >= max.month));
  const prev = shiftPeriod(year, month, -1);
  const showYear = year !== new Date().getFullYear();

  return (
    <div className="flex items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight capitalize">
          {formatMonthName(month)}
          {showYear && <span className="text-muted-foreground"> {year}</span>}
        </h1>
        <p className="text-sm text-muted-foreground">
          {range ? formatPeriodRange(range.startDate, range.lastDate) : ' '}
          {meta && <> · {meta}</>}
        </p>
      </div>
      <div className="flex shrink-0 gap-1">
        <Button
          type="button"
          onClick={() => onChange(prev.year, prev.month)}
          aria-label="Föregående period"
          variant="ghost"
          size="icon"
          className="rounded-full text-muted-foreground"
        >
          <ChevronLeft />
        </Button>
        <Button
          type="button"
          onClick={() => onChange(next.year, next.month)}
          disabled={atMax}
          aria-label="Nästa period"
          variant="ghost"
          size="icon"
          className="rounded-full text-muted-foreground"
        >
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
}
